import React, { useState, useEffect } from 'react';
import { Spinner, Alert, Button } from 'react-bootstrap';
import { FaChevronLeft, FaChevronRight, FaExpand, FaCompress } from 'react-icons/fa';
import { GlobalWorkerOptions, getDocument } from 'pdfjs-dist';
import './PDFImageViewer.css';

// Set worker path for PDF.js - use CDN to avoid build issues
GlobalWorkerOptions.workerSrc = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js';

interface PDFImageViewerProps {
  documentUrl: string;
  documentId: string;
  onPageChange?: (page: number) => void;
  onImagesLoaded?: (images: ImageData[]) => void;
  children?: React.ReactNode; // For field overlays
}

interface ImageData {
  page: number;
  url: string;
  width: number;
  height: number;
}

// Already set at the top of the file
// GlobalWorkerOptions.workerSrc is set to CDN URL above

const PDFImageViewer: React.FC<PDFImageViewerProps> = ({
  documentUrl,
  documentId,
  onPageChange,
  onImagesLoaded,
  children
}) => {
  const [images, setImages] = useState<ImageData[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [scale, setScale] = useState(1);
  const [reloadCounter, setReloadCounter] = useState(0);

  useEffect(() => {
    let cancelled = false;

    const loadPdf = async () => {
      try {
        setLoading(true);
        setError(null);

        const token = localStorage.getItem('token') || localStorage.getItem('auth_token');
        const loadingTask = getDocument({
          url: documentUrl,
          withCredentials: true,
          httpHeaders: token ? { Authorization: `Bearer ${token}` } : undefined
        });
        const pdf = await loadingTask.promise;

        const renderedPages: ImageData[] = [];

        for (let pageNumber = 1; pageNumber <= pdf.numPages; pageNumber++) {
          if (cancelled) break;

          const page = await pdf.getPage(pageNumber);
          const viewport = page.getViewport({ scale: 1.5 });
          const canvas = document.createElement('canvas');
          const context = canvas.getContext('2d');

          if (!context) {
            throw new Error('Unable to render PDF page context');
          }

          canvas.width = viewport.width;
          canvas.height = viewport.height;

          await page.render({ canvasContext: context, viewport }).promise;

          renderedPages.push({
            page: pageNumber,
            url: canvas.toDataURL('image/png'),
            width: viewport.width,
            height: viewport.height
          });
        }

        if (cancelled) {
          return;
        }

        setImages(renderedPages);
        setCurrentPage(1);
        if (onImagesLoaded) {
          onImagesLoaded(renderedPages);
        }
      } catch (err: any) {
        console.error('Error loading PDF:', err);
        if (!cancelled) {
          setError(err?.message || 'Failed to load document');
          setImages([]);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadPdf();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [documentUrl, documentId, onImagesLoaded, reloadCounter]);

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
    if (onPageChange) {
      onPageChange(newPage);
    }
  };

  const toggleFullscreen = () => {
    const viewer = document.getElementById('pdf-image-viewer');
    if (!viewer) return;

    if (!isFullscreen) {
      if (viewer.requestFullscreen) {
        viewer.requestFullscreen();
      }
    } else if (document.fullscreenElement) {
      document.exitFullscreen?.();
    }
    setIsFullscreen(!isFullscreen);
  };

  const handleZoom = (delta: number) => {
    const newScale = Math.max(0.5, Math.min(3, scale + delta));
    setScale(newScale);
  };

  if (loading) {
    return (
      <div className="pdf-image-viewer-loading">
        <Spinner animation="border" variant="primary" />
        <p className="mt-3">Loading document...</p>
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="danger" className="m-3">
        <Alert.Heading>Error Loading Document</Alert.Heading>
        <p>{error}</p>
        <Button
          variant="outline-danger"
          onClick={() => setReloadCounter((count) => count + 1)}
        >
          Try Again
        </Button>
      </Alert>
    );
  }

  if (images.length === 0) {
    return (
      <Alert variant="warning" className="m-3">
        No pages found in this document
      </Alert>
    );
  }

  const currentImage = images[currentPage - 1];

  return (
    <div id="pdf-image-viewer" className="pdf-image-viewer">
      {/* Controls */}
      <div className="pdf-image-controls">
        <div className="page-navigation">
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
          >
            <FaChevronLeft />
          </Button>
          <span className="page-info">
            Page {currentPage} of {images.length}
          </span>
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === images.length}
          >
            <FaChevronRight />
          </Button>
        </div>

        <div className="zoom-controls">
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={() => handleZoom(-0.1)}
          >
            -
          </Button>
          <span className="zoom-info">{Math.round(scale * 100)}%</span>
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={() => handleZoom(0.1)}
          >
            +
          </Button>
          <Button
            variant="outline-secondary"
            size="sm"
            className="ms-2"
            onClick={toggleFullscreen}
          >
            {isFullscreen ? <FaCompress /> : <FaExpand />}
          </Button>
        </div>
      </div>

      {/* Image Display */}
      <div className="pdf-image-container">
        <div
          className="pdf-image-wrapper"
          style={{
            transform: `scale(${scale})`,
            transformOrigin: 'center center'
          }}
        >
          <div className="pdf-page-container" style={{ position: 'relative', display: 'inline-block' }}>
            <img
              src={currentImage.url}
              alt={`Page ${currentImage.page}`}
              style={{
                width: currentImage.width,
                height: currentImage.height,
                display: 'block'
              }}
            />
            {children}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PDFImageViewer;
